import type { Map as MapType, GeoJSONSource } from "maplibre-gl";
import { greatCircle, circle, destination } from "@turf/turf";
import type { MapEventBus } from "../../core/MapEventBus";
import type {
  GradientStop,
  LinePrimitive,
  Position,
  PrimitiveStyle,
  PrimitiveTarget,
  ShapePrimitive,
} from "../../types";
import { PrimitivesSelection } from "./PrimitivesSelection";

// Which GeoJSON source this manager writes features into. All primitive layers
// (fill + the three dash-style line layers) read from this single source and
// fan out via their own filters — see layers/primitives.ts.
export interface PrimitivesManagerConfig {
  sourceId: string;
}

export class PrimitivesManager {
  private primitiveCache = new Map<string, PrimitiveTarget>();
  private flushScheduled = false;
  private rafId: number | null = null;
  private selection: PrimitivesSelection;

  constructor(
    private map: MapType,
    private eventBus: MapEventBus,
    private config: PrimitivesManagerConfig,
  ) {
    this.selection = new PrimitivesSelection(eventBus);
    this.setupListeners();
  }

  private setupListeners() {
    // Selection changes restyle affected primitives on the next frame.
    this.eventBus.on("primitive:selected", () => this.scheduleFlush());
    this.eventBus.on("primitive:deselected", () => this.scheduleFlush());
  }

  // Coalesce a burst of updates into a single flush on the next animation
  // frame (~16ms) so primitives render near-instantly instead of waiting on a
  // polling interval.
  private scheduleFlush() {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    this.rafId = requestAnimationFrame(() => {
      this.flushScheduled = false;
      this.rafId = null;
      this.flushUpdates();
    });
  }

  // ── Public API (called by PrimitivesUpdater via the /primitives WS topic) ──

  updatePrimitive(target: PrimitiveTarget) {
    this.primitiveCache.set(target.primitive_id, target);
    this.scheduleFlush();
  }

  removePrimitive(primitive_id: string) {
    this.primitiveCache.delete(primitive_id);
    if (this.selection.isSelected(primitive_id)) {
      this.selection.deselectPrimitive(primitive_id);
    }
    this.scheduleFlush();
  }

  clear() {
    this.primitiveCache.clear();
    this.scheduleFlush();
  }

  // ── Rendering ──

  private flushUpdates() {
    const source = this.map.getSource(this.config.sourceId) as
      | GeoJSONSource
      | undefined;
    if (!source || !this.map.isStyleLoaded()) {
      // Style not ready yet — retry on the next frame.
      this.scheduleFlush();
      return;
    }

    const features: GeoJSON.Feature[] = [];
    for (const target of this.primitiveCache.values()) {
      if (target.kind === "line") {
        this.buildLineFeatures(target, features);
      } else {
        this.buildShapeFeature(target, features);
      }
    }

    try {
      source.setData({ type: "FeatureCollection", features });
    } catch (err) {
      // Source can briefly exist before the style is ready; retry next frame.
      console.warn("[PrimitivesManager] setData deferred:", err);
      this.scheduleFlush();
    }
  }

  private buildLineFeatures(line: LinePrimitive, out: GeoJSON.Feature[]) {
    const coords = this.buildLineCoords(line.points ?? [], line.geodesic);
    if (coords.length < 2) return;

    const props = this.styleProps(line.style);

    // Gradient: MapLibre can't apply a per-feature gradient, so subdivide the
    // line into solid colored segments interpolated across the stops.
    if (line.style?.gradient?.stops?.length) {
      this.buildGradientSegments(
        line.primitive_id,
        coords,
        line.style.gradient.stops,
        props,
        out,
      );
      return;
    }

    out.push({
      id: line.primitive_id,
      type: "Feature",
      geometry: { type: "LineString", coordinates: coords },
      properties: props,
    });
  }

  private buildShapeFeature(shape: ShapePrimitive, out: GeoJSON.Feature[]) {
    const ring = this.buildShapeRing(shape);
    if (!ring) return;

    out.push({
      id: shape.primitive_id,
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: this.styleProps(shape.style),
    });
  }

  // ── Geometry builders ──

  private buildLineCoords(
    points: Position[],
    geodesic?: boolean,
  ): number[][] {
    if (points.length < 2) return [];

    const coords: number[][] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a: number[] = [points[i].lon, points[i].lat];
      const b: number[] = [points[i + 1].lon, points[i + 1].lat];

      if (geodesic) {
        const gc = greatCircle(a, b, { npoints: 64 });
        const g = gc.geometry;
        let seg: number[][] =
          g.type === "MultiLineString"
            ? (g.coordinates[0] as number[][])
            : (g.coordinates as number[][]);
        // Drop the first vertex on subsequent segments to avoid duplicates.
        if (i > 0) seg = seg.slice(1);
        coords.push(...seg);
      } else {
        if (i === 0) coords.push(a);
        coords.push(b);
      }
    }
    return coords;
  }

  private buildShapeRing(shape: ShapePrimitive): number[][] | null {
    const center: number[] = [shape.center.lon, shape.center.lat];
    const radiusKm = (shape.radius_meters ?? 1000) / 1000;
    const rotation = shape.rotation_deg ?? 0;

    if (shape.kind === "circle") {
      const c = circle(center, radiusKm, { steps: 64, units: "kilometers" });
      return c.geometry.coordinates[0] as number[][];
    }

    // square (4 verts, offset 45° so it reads axis-aligned), triangle (3),
    // generic polygon (`sides`).
    const sides =
      shape.kind === "triangle"
        ? 3
        : shape.kind === "square"
          ? 4
          : Math.max(3, shape.sides ?? 5);
    const startAngle = shape.kind === "square" ? 45 : 0;

    const ring: number[][] = [];
    for (let i = 0; i < sides; i++) {
      const bearing = startAngle + rotation + (360 / sides) * i;
      const d = destination(center, radiusKm, bearing, {
        units: "kilometers",
      });
      ring.push(d.geometry.coordinates as number[]);
    }
    ring.push(ring[0]); // close the ring
    return ring;
  }

  // ── Styling ──

  // Maps a PrimitiveStyle onto feature properties the layers read via
  // ["get", ...]. Undefined values are omitted so the layer `coalesce`
  // fallbacks apply. `lineStyle` always present so the feature routes to a
  // line layer (and gets a polygon outline for shapes).
  private styleProps(style?: PrimitiveStyle): Record<string, unknown> {
    return {
      color: style?.color,
      opacity: style?.opacity,
      width: style?.width,
      lineStyle: style?.lineStyle ?? "solid",
      fillColor: style?.fillColor,
      fillOpacity: style?.fillOpacity,
    };
  }

  private buildGradientSegments(
    primitiveId: string,
    coords: number[][],
    stops: GradientStop[],
    baseProps: Record<string, unknown>,
    out: GeoJSON.Feature[],
  ) {
    // Cumulative planar length along the line — good enough to map color stops.
    const cum: number[] = [0];
    for (let i = 1; i < coords.length; i++) {
      const dx = coords[i][0] - coords[i - 1][0];
      const dy = coords[i][1] - coords[i - 1][1];
      cum.push(cum[i - 1] + Math.hypot(dx, dy));
    }
    const total = cum[cum.length - 1] || 1;

    for (let i = 0; i < coords.length - 1; i++) {
      const t = (cum[i] + cum[i + 1]) / 2 / total;
      out.push({
        id: `${primitiveId}-seg-${i}`,
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [coords[i], coords[i + 1]],
        },
        properties: {
          ...baseProps,
          color: sampleGradient(stops, t),
          lineStyle: "solid",
        },
      });
    }
  }

  destroy() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.selection.destroy();
    this.primitiveCache.clear();
  }
}

// ── Gradient color helpers ──

function parseColor(c: string): [number, number, number, number] {
  const s = c.trim();
  if (s.startsWith("#")) {
    let hex = s.slice(1);
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((x) => x + x)
        .join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return [r, g, b, a];
  }
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const p = m[1].split(",").map((v) => parseFloat(v.trim()));
    return [p[0] || 0, p[1] || 0, p[2] || 0, p[3] === undefined ? 1 : p[3]];
  }
  return [255, 255, 255, 1];
}

function sampleGradient(stops: GradientStop[], t: number): string {
  const s = [...stops].sort((a, b) => a[0] - b[0]);
  if (t <= s[0][0]) return s[0][1];
  if (t >= s[s.length - 1][0]) return s[s.length - 1][1];

  for (let i = 0; i < s.length - 1; i++) {
    if (t >= s[i][0] && t <= s[i + 1][0]) {
      const span = s[i + 1][0] - s[i][0] || 1;
      const local = (t - s[i][0]) / span;
      const c0 = parseColor(s[i][1]);
      const c1 = parseColor(s[i + 1][1]);
      const r = Math.round(c0[0] + (c1[0] - c0[0]) * local);
      const g = Math.round(c0[1] + (c1[1] - c0[1]) * local);
      const b = Math.round(c0[2] + (c1[2] - c0[2]) * local);
      const a = c0[3] + (c1[3] - c0[3]) * local;
      return `rgba(${r},${g},${b},${a})`;
    }
  }
  return s[s.length - 1][1];
}
